import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

function applySecurityHeaders(response: Response, pathname: string): Response {
  const headers = new Headers(response.headers);
  // Baseline hardening on every response.
  headers.set("X-Content-Type-Options", "nosniff");
  headers.set("Referrer-Policy", "strict-origin-when-cross-origin");
  headers.set(
    "Permissions-Policy",
    "camera=(self), microphone=(self), geolocation=(), payment=(self)",
  );
  headers.set("X-Frame-Options", "SAMEORIGIN");

  // Cache-Control: never cache admin/portal/api; short SWR on marketing HTML.
  if (!headers.has("cache-control")) {
    const isPrivate =
      pathname.startsWith("/admin") ||
      pathname.startsWith("/portal") ||
      pathname.startsWith("/api") ||
      pathname.startsWith("/lovable") ||
      pathname.startsWith("/auth") ||
      pathname.startsWith("/reset-password");
    const ct = headers.get("content-type") || "";
    if (isPrivate) {
      headers.set("Cache-Control", "private, no-store");
    } else if (ct.includes("text/html")) {
      headers.set("Cache-Control", "public, max-age=0, s-maxage=300, stale-while-revalidate=86400");
    }
  }
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/") || url.pathname === "/email/unsubscribe") {
    return next();
  }
  try {
    const res = await next();
    if (res instanceof Response) return applySecurityHeaders(res, url.pathname);
    return res;
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return applySecurityHeaders(
      new Response(renderErrorPage(), {
        status: 500,
        headers: { "content-type": "text/html; charset=utf-8" },
      }),
      url.pathname,
    );
  }
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [errorMiddleware],
}));
